import React, { useEffect, useState } from 'react';
import api from '../services/api';
import {
  RiChatSmile2Line, RiSendPlaneLine, RiCheckDoubleLine,
  RiTimeLine, RiChat1Line,
} from 'react-icons/ri';

interface Feedback {
  id: number;
  session_id: number;
  message: string;
  reponse: string | null;
  created_at: string;
}

const AdminFeedbacks: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [replyInputs, setReplyInputs] = useState<{ [key: number]: string }>({});
  const [sending, setSending] = useState<{ [key: number]: boolean }>({});

  useEffect(() => {
    api.get('/feedbacks/').then(r => setFeedbacks(r.data)).catch(console.error);
  }, []);

  const handleReplyChange = (id: number, value: string) => {
    setReplyInputs(prev => ({ ...prev, [id]: value }));
  };

  const submitReply = async (id: number) => {
    const reply = replyInputs[id]?.trim();
    if (!reply) return;
    setSending(prev => ({ ...prev, [id]: true }));
    try {
      await api.put(`/feedbacks/${id}/reply?reponse=${encodeURIComponent(reply)}`);
      const res = await api.get('/feedbacks/');
      setFeedbacks(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setSending(prev => ({ ...prev, [id]: false }));
    }
  };

  const pending = feedbacks.filter(f => !f.reponse);
  const answered = feedbacks.filter(f => f.reponse);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Page header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Retours clients</h1>
          <p className="text-sm text-gray-500 mt-1">Lisez et répondez aux messages de vos clientes</p>
        </div>
        <div className="flex gap-3 text-sm">
          <span className="flex items-center gap-1.5 bg-orange-50 text-orange-600 px-3 py-1.5 rounded-full font-medium">
            <RiChat1Line /> {pending.length} en attente
          </span>
          <span className="flex items-center gap-1.5 bg-green-50 text-green-600 px-3 py-1.5 rounded-full font-medium">
            <RiCheckDoubleLine /> {answered.length} répondus
          </span>
        </div>
      </div>

      {feedbacks.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm h-64 flex flex-col items-center justify-center text-gray-400">
          <RiChatSmile2Line className="text-4xl mb-3 text-gray-300" />
          <p className="text-sm">Aucun retour client pour le moment.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {feedbacks.map(fb => (
            <div
              key={fb.id}
              className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all ${
                fb.reponse ? 'border-gray-100' : 'border-orange-100'
              }`}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center">
                    <RiChatSmile2Line className="text-brand-500 text-sm" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    Session <span className="text-brand-600">#{fb.session_id}</span>
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <RiTimeLine />
                    {new Date(fb.created_at).toLocaleString('fr-FR', {
                      day: '2-digit', month: 'short', year: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    })}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    fb.reponse
                      ? 'bg-green-50 text-green-600'
                      : 'bg-orange-50 text-orange-600'
                  }`}>
                    {fb.reponse ? 'Répondu' : 'En attente'}
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="px-5 py-4 space-y-3">
                {/* Client message */}
                <div className="flex gap-3">
                  <div className="w-1 rounded-full bg-gray-200 flex-shrink-0" />
                  <p className="text-sm text-gray-700 italic leading-relaxed">
                    {fb.message ? `"${fb.message}"` : (
                      <span className="text-gray-400 not-italic">Aucun message laissé par la cliente.</span>
                    )}
                  </p>
                </div>

                {/* Reply */}
                {fb.reponse ? (
                  <div className="flex gap-3">
                    <div className="w-1 rounded-full bg-brand-300 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-medium text-brand-600 mb-1 flex items-center gap-1">
                        <RiCheckDoubleLine /> Votre réponse
                      </p>
                      <p className="text-sm text-gray-700 leading-relaxed">{fb.reponse}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2 mt-3">
                    <input
                      type="text"
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition"
                      placeholder="Écrire une réponse à cette cliente…"
                      value={replyInputs[fb.id] || ''}
                      onChange={(e) => handleReplyChange(fb.id, e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && submitReply(fb.id)}
                    />
                    <button
                      onClick={() => submitReply(fb.id)}
                      disabled={sending[fb.id] || !replyInputs[fb.id]?.trim()}
                      className="flex items-center gap-1.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RiSendPlaneLine />
                      {sending[fb.id] ? '…' : 'Envoyer'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminFeedbacks;
